import { WarningIcon } from '@plato/ui-legacy/assets/icons/warning-icon';
import { BaseConfirmationDialog } from '@plato/ui-legacy/components/dialog/confirmation-dialog';
import { DialogStateManager, useDialog } from '@plato/ui-legacy/components/dialog/dialog-provider';
import { useToast } from '@plato/ui-legacy/components/ui/use-toast';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { CrmCommentDTO } from '../../../server/dto/crm-comment-dto.ts';
import { api } from '../../../utils/api.ts';
import type { ModalInnerProps } from '../../modal/index.tsx';

export type DeleteCommentDialogProps = {
    comment: CrmCommentDTO;
    onDeleted?: () => void;
} & ModalInnerProps;

export const DeleteCommentDialog = DialogStateManager.create<DeleteCommentDialogProps>(
    ({ comment, onDeleted }: DeleteCommentDialogProps) => {
        const { t } = useTranslation('tasks');
        const { toast } = useToast();
        const [isDeletingComment, setIsDeletingComment] = useState(false);
        const modal = useDialog();
        const apiUtils = api.useUtils();

        const { commentId } = comment;

        const deleteMutation = api.platoUser.command.comments.delete.useMutation({
            onSuccess() {
                const key = comment.taskId
                    ? { taskId: comment.taskId }
                    : comment.noteId
                      ? { noteId: comment.noteId }
                      : undefined;
                if (!key) {
                    return;
                }
                // Optimistically update the cache by removing the deleted comment
                apiUtils.platoUser.query.comments.getManyComments.setData(key, (oldData) => {
                    if (!oldData) {
                        return [];
                    }
                    return oldData.filter((comment) => comment.commentId !== commentId);
                });

                toast({
                    icon: 'success',
                    title: t('comments__toast__deleted_success_title'),
                });

                onDeleted?.();
                modal.remove();
            },
            onError: (error) => {
                toast({
                    icon: 'warning',
                    title: 'Error',
                    description: error.message,
                });
                setIsDeletingComment(false);
            },
        });

        const handleSubmit = async () => {
            setIsDeletingComment(true);
            deleteMutation.mutate({ commentId });
        };

        const handleCloseConfirmation = () => {
            modal.remove();
        };

        return (
            <BaseConfirmationDialog
                focus="destroy"
                title={t('comments__delete_confirmation__title')}
                description={t('comments__delete_confirmation__body')}
                icon={WarningIcon}
            >
                <BaseConfirmationDialog.Actions>
                    <BaseConfirmationDialog.RejectButton onClick={handleCloseConfirmation}>
                        {t('form__cancel', {
                            ns: 'common',
                        })}
                    </BaseConfirmationDialog.RejectButton>
                    <BaseConfirmationDialog.AcceptButton onClick={handleSubmit} isLoading={isDeletingComment}>
                        {t('form__delete', {
                            ns: 'common',
                        })}
                    </BaseConfirmationDialog.AcceptButton>
                </BaseConfirmationDialog.Actions>
            </BaseConfirmationDialog>
        );
    },
);
